/**
 * Utility functions for StakeWise staking validation.
 * @internal
 */
import { VAULT_ADDRESSES_MAP, compareEthVaultAddresses, ethereumMainnetChainID, normalizeEthVaultAddress, } from "../../../../../shared/vault-addresses";
/**
 * Resolves the vault allow list for the given chain from the customer's Ethereum config.
 * Returns an empty array when no config is provided (no restriction).
 */
function getEthAllowList(chainId, ethConfig) {
    if (!ethConfig)
        return [];
    return chainId === ethereumMainnetChainID
        ? ethConfig.customerMainnetVaultAddresses
        : ethConfig.customerTestnetVaultAddresses;
}
/**
 * Validates whether a vault address is permitted by the customer's Ethereum config.
 *
 * - If no config is set, all addresses are allowed (returns `true`).
 * - If the config is set but the allow list is empty, all addresses are allowed (returns `true`).
 * - If the allow list is non-empty, the operator must appear in it (case-insensitive).
 *
 * @param chainId  - CAIP-2 chain ID (e.g. "eip155:1")
 * @param operator - Vault address to validate
 * @param ethConfig - Customer Ethereum config, or undefined for no restriction
 * @returns `true` if the address is allowed, `false` otherwise
 */
export function _isVaultAddressAllowed(chainId, operator, ethConfig) {
    const allowList = getEthAllowList(chainId, ethConfig);
    if (allowList.length === 0)
        return true;
    const normalized = normalizeEthVaultAddress(operator);
    return allowList.some((a) => compareEthVaultAddresses(a, normalized));
}
/**
 * Returns the first configured vault address for the given chain, or `undefined`
 * when no config is set / the allow list is empty.
 */
export function getConfiguredDefaultVault(chainId, ethConfig) {
    return getEthAllowList(chainId, ethConfig)[0];
}
/**
 * Resolves the vault address for a request: validates `operator` against the
 * allow list (if set) then falls back to the configured default and finally the
 * hard-coded map.
 *
 * @throws if `operator` is not in the allow list
 * @throws if no vault address can be determined for `chainId`
 */
export function resolveVaultAddress(chainId, operator, ethConfig) {
    if (operator && !_isVaultAddressAllowed(chainId, operator, ethConfig)) {
        throw new Error(`Vault address ${operator} is not in the allowed list for chain ${chainId}`);
    }
    const vaultAddress = operator || getConfiguredDefaultVault(chainId, ethConfig) || VAULT_ADDRESSES_MAP[chainId];
    if (!vaultAddress) {
        throw new Error(`Vault address not found for chain ID: ${chainId}`);
    }
    return vaultAddress;
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidXRpbC5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uLy4uLy4uLy4uLy4uLy4uL3NyYy9tYWluL21vZHVsZXMvc3Rha2luZy9wcm90b2NvbHMvc3Rha2V3aXNlL2ludGVybmFsL3V0aWwudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7OztHQUdHO0FBR0gsT0FBTyxFQUNMLG1CQUFtQixFQUNuQix3QkFBd0IsRUFDeEIsc0JBQXNCLEVBQ3RCLHdCQUF3QixHQUN6QixNQUFNLHVDQUF1QyxDQUFDO0FBRS9DOzs7R0FHRztBQUNILFNBQVMsZUFBZSxDQUFDLE9BQWUsRUFBRSxTQUFzQztJQUM5RSxJQUFJLENBQUMsU0FBUztRQUFFLE9BQU8sRUFBRSxDQUFDO0lBQzFCLE9BQU8sT0FBTyxLQUFLLHNCQUFzQjtRQUN2QyxDQUFDLENBQUMsU0FBUyxDQUFDLDZCQUE2QjtRQUN6QyxDQUFDLENBQUMsU0FBUyxDQUFDLDZCQUE2QixDQUFDO0FBQzlDLENBQUM7QUFFRDs7Ozs7Ozs7Ozs7R0FXRztBQUNILE1BQU0sVUFBVSxzQkFBc0IsQ0FDcEMsT0FBZSxFQUNmLFFBQWdCLEVBQ2hCLFNBQXNDO0lBRXRDLE1BQU0sU0FBUyxHQUFHLGVBQWUsQ0FBQyxPQUFPLEVBQUUsU0FBUyxDQUFDLENBQUM7SUFDdEQsSUFBSSxTQUFTLENBQUMsTUFBTSxLQUFLLENBQUM7UUFBRSxPQUFPLElBQUksQ0FBQztJQUN4QyxNQUFNLFVBQVUsR0FBRyx3QkFBd0IsQ0FBQyxRQUFRLENBQUMsQ0FBQztJQUN0RCxPQUFPLFNBQVMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLEVBQUUsRUFBRSxDQUFDLHdCQUF3QixDQUFDLENBQUMsRUFBRSxVQUFVLENBQUMsQ0FBQyxDQUFDO0FBQ3hFLENBQUM7QUFFRDs7O0dBR0c7QUFDSCxNQUFNLFVBQVUseUJBQXlCLENBQ3ZDLE9BQWUsRUFDZixTQUFzQztJQUV0QyxPQUFPLGVBQWUsQ0FBQyxPQUFPLEVBQUUsU0FBUyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7QUFDaEQsQ0FBQztBQUVEOzs7Ozs7O0dBT0c7QUFDSCxNQUFNLFVBQVUsbUJBQW1CLENBQ2pDLE9BQWUsRUFDZixRQUE0QixFQUM1QixTQUFzQztJQUV0QyxJQUFJLFFBQVEsSUFBSSxDQUFDLHNCQUFzQixDQUFDLE9BQU8sRUFBRSxRQUFRLEVBQUUsU0FBUyxDQUFDLEVBQUUsQ0FBQztRQUN0RSxNQUFNLElBQUksS0FBSyxDQUFDLGlCQUFpQixRQUFRLHlDQUF5QyxPQUFPLEVBQUUsQ0FBQyxDQUFDO0lBQy9GLENBQUM7SUFDRCxNQUFNLFlBQVksR0FDaEIsUUFBUSxJQUFJLHlCQUF5QixDQUFDLE9BQU8sRUFBRSxTQUFTLENBQUMsSUFBSSxtQkFBbUIsQ0FBQyxPQUFPLENBQUMsQ0FBQztJQUM1RixJQUFJLENBQUMsWUFBWSxFQUFFLENBQUM7UUFDbEIsTUFBTSxJQUFJLEtBQUssQ0FBQyx5Q0FBeUMsT0FBTyxFQUFFLENBQUMsQ0FBQztJQUN0RSxDQUFDO0lBQ0QsT0FBTyxZQUFZLENBQUM7QUFDdEIsQ0FBQyJ9